import { Box, Stack } from '@mui/material';
import { ReactNode, RefObject } from 'react';
import { TabItem } from '../../../components/v2/ui/Tabs/tabs.types';
import {
  EntityWarning,
  GlobalSearchView,
  ResultsFooterMode,
  SearchableEntityType,
  SuggestedCatalog,
} from '../globalSearch.types';
import { GlobalSearchListItem } from '../staticCommands/staticCommands.types';
import EntityWarningBanner from './EntityWarningBanner';
import { GlobalSearchItemActions } from './ResultRow/resultRow.types';
import ResultsList from './ResultsList';
import ResultsLoadMore from './ResultsLoadMore';
import ResultsNavFooter from './ResultsNavFooter';
import ResultsTabs from './ResultsTabs';
import ErrorState from './states/ErrorState';
import LoadingState from './states/LoadingState';
import NoResults from './states/NoResults';
import RecentSearches from './states/RecentSearches';
import SuggestedCatalogs from './states/SuggestedCatalogs';

interface RecentsConfig {
  searches: string[];
  onSelect: (query: string) => void;
  onRemove: (query: string) => void;
}

interface SuggestedCatalogsConfig {
  catalogs: SuggestedCatalog[];
  onSelect: (catalogId: string) => void;
}

interface ResultsConfig {
  isMobile: boolean;
  ariaLabel: string;
  query: string;
  tabs: TabItem[];
  activeTabId: string;
  onSelectTab: (id: string) => void;
  items: GlobalSearchListItem[];
  activeIndex: number;
  isKeyboardNavActive: boolean;
  onMouseMove: () => void;
  isDimmed: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  itemActions?: GlobalSearchItemActions;
  canOpenEntity: (entityType: SearchableEntityType) => boolean;
  onSelectItem: (item: GlobalSearchListItem) => void;
  footerMode: ResultsFooterMode;
  footerTerm: string;
  activeEntityLabel: string;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onNavigateToList: () => void;
}

interface GlobalSearchBodyProps {
  view: GlobalSearchView;
  recents: RecentsConfig;
  suggestedCatalogs: SuggestedCatalogsConfig;
  results: ResultsConfig;
  warning: EntityWarning | null;
}

function GlobalSearchBody({
  view,
  recents,
  suggestedCatalogs,
  results,
  warning,
}: GlobalSearchBodyProps) {
  const renderEmpty = (): ReactNode => {
    const hasRecents = recents.searches.length > 0;
    const hasSuggestedCatalogs = suggestedCatalogs.catalogs.length > 0;
    if (!hasRecents && !hasSuggestedCatalogs) {
      return null;
    }
    return (
      <Stack sx={{ gap: 1 }}>
        {hasRecents && (
          <RecentSearches
            searches={recents.searches}
            onSelect={recents.onSelect}
            onRemove={recents.onRemove}
          />
        )}
        {hasSuggestedCatalogs && (
          <SuggestedCatalogs
            catalogs={suggestedCatalogs.catalogs}
            onSelect={suggestedCatalogs.onSelect}
          />
        )}
      </Stack>
    );
  };

  const renderNoResult = (): ReactNode =>
    warning ? (
      <EntityWarningBanner
        entityLabel={warning.entityLabel}
        isRetrying={warning.isRetrying}
        onRetry={warning.onRetry}
      />
    ) : (
      <NoResults />
    );

  const renderListFooter = (): ReactNode => {
    if (results.footerMode === 'load-more' && results.isMobile) {
      return (
        <ResultsLoadMore
          isMobile
          hasMore={results.hasMore}
          isLoading={results.isLoadingMore}
          rootRef={results.scrollRef}
          onLoadMore={results.onLoadMore}
        />
      );
    }
    if (results.footerMode === 'nav') {
      return (
        <ResultsNavFooter
          term={results.footerTerm}
          entityLabel={results.activeEntityLabel}
          onNavigate={results.onNavigateToList}
        />
      );
    }
    return undefined;
  };

  const renderResults = (): ReactNode => (
    <>
      <Box sx={{ flexShrink: 0 }}>
        <ResultsTabs
          tabs={results.tabs}
          activeTabId={results.activeTabId}
          onSelect={results.onSelectTab}
        />
      </Box>
      {warning && (
        <EntityWarningBanner
          entityLabel={warning.entityLabel}
          isRetrying={warning.isRetrying}
          onRetry={warning.onRetry}
        />
      )}
      <ResultsList
        items={results.items}
        query={results.query}
        ariaLabel={results.ariaLabel}
        activeIndex={results.activeIndex}
        isDimmed={results.isDimmed}
        scrollRef={results.scrollRef}
        itemActions={results.itemActions}
        canOpenEntity={results.canOpenEntity}
        onSelectItem={results.onSelectItem}
        keyboardNavigation={{
          isActive: results.isKeyboardNavActive,
          onMouseMove: results.onMouseMove,
        }}
        footer={renderListFooter()}
      />
      {results.footerMode === 'load-more' && !results.isMobile && (
        <ResultsLoadMore
          isMobile={false}
          hasMore={results.hasMore}
          isLoading={results.isLoadingMore}
          rootRef={results.scrollRef}
          onLoadMore={results.onLoadMore}
        />
      )}
    </>
  );

  const renderByView: Record<GlobalSearchView, () => ReactNode> = {
    empty: renderEmpty,
    loading: () => <LoadingState />,
    error: () => <ErrorState />,
    'no-result': renderNoResult,
    results: renderResults,
  };

  return <>{renderByView[view]()}</>;
}

export default GlobalSearchBody;
